import React, { useState, useEffect } from 'react';
import api from '../api';
import { formatIndian } from '../utils/formatNumber';
import CollapsibleFilters from './CollapsibleFilters';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import ProductTypeahead from './ProductTypeahead';

interface JobOrderItem {
  id?: number;
  product_id: number;
  product_sku?: string;
  product_name?: string;
  quantity: number;
  inward_product_id?: number;
  inward_product_sku?: string;
  inward_product_name?: string;
  inward_quantity?: number;
  notes?: string;
}

interface JobOrder {
  id: number;
  job_order_number: string;
  type: string;
  status: string;
  decorator_name?: string;
  decoration_type?: string;
  expected_return_date?: string;
  actual_return_date?: string;
  notes?: string;
  related_job_order_id?: number;
  related_job_order_ids?: number[];
  related_job_order_number?: string;
  linked_outwards?: Array<{id: number; job_order_number: string; status: string; decorator_name?: string; created_at?: string}>;
  received_qty?: number;
  linked_inwards?: Array<{id: number; job_order_number: string; status: string; created_at?: string; total_qty: number}>;
  items: JobOrderItem[];
  dc_id?: number;
  dc_number?: string;
  created_at?: string;
}

const JobOrderManager: React.FC<{ userRole?: string }> = ({ userRole = 'viewer' }) => {
  const [orders, setOrders] = useState<JobOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState<'outward' | 'inward'>('outward');
  const [products, setProducts] = useState<Array<{id: number; sku: string; name: string; price?: number; attributes?: Array<{attribute_name: string; value: string}>}>>([]);

  // Helper: format product label as "SKU - Name - Color - Decoration - Size"
  const getProductLabel = (product: {sku: string; name: string; price?: number; attributes?: Array<{attribute_name: string; value: string}>}) => {
    const color = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'color')?.value || '';
    const decoration = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value || '';
    const size = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'size')?.value || '';
    let label = `${product.sku} - ${product.name}`;
    if (color) label += ` - ${color}`;
    if (decoration) label += ` - ${decoration}`;
    if (size) label += ` - ${size}`;
    return label;
  };
  const [submitting, setSubmitting] = useState(false);
  const [viewingJobOrder, setViewingJobOrder] = useState<JobOrder | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});
  const [warehouses, setWarehouses] = useState<Array<{id: number; code: string; name: string; is_default?: boolean}>>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number>(0);

  // DC (Delivery Challan) state
  const [showDCForm, setShowDCForm] = useState(false);
  const [dcJobOrder, setDcJobOrder] = useState<JobOrder | null>(null);
  const [dcSaving, setDcSaving] = useState(false);

  // Edit Job Order state
  const [editingJobOrder, setEditingJobOrder] = useState<JobOrder | null>(null);
  const [editForm, setEditForm] = useState({
    decorator_name: '',
    decoration_type: '',
    expected_return_date: '',
    actual_return_date: '',
    notes: '',
  });
  const [editItems, setEditItems] = useState<Array<{product_id: number; product_sku: string; product_name: string; quantity: number; inward_product_id: string; inward_quantity: string; inward_product_label?: string}>>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorResults, setVendorResults] = useState<Array<{id: number; name: string; address?: string; city?: string; state?: string; pincode?: string; gstin?: string}>>([]);
  const [dcForm, setDcForm] = useState({
    to_name: '',
    to_address: '',
    to_city: '',
    to_state: '',
    to_pincode: '',
    to_gstin: '',
    vehicle_number: '',
    transport_mode: 'road',
    notes: '',
  });

  // Filters (Change #2)
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('draft,confirmed');
  const [filterDecorator, setFilterDecorator] = useState<string>('');
  const [decoratorOptions, setDecoratorOptions] = useState<string[]>([]);
  // Search (Change #3)
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Form state
  const [decoratorName, setDecoratorName] = useState('');
  const [decoratorVendorResults, setDecoratorVendorResults] = useState<Array<{id: number; name: string}>>([]);
  const [showDecoratorDropdown, setShowDecoratorDropdown] = useState(false);
  const [decorationType, setDecorationType] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formItems, setFormItems] = useState<Array<{product_id: string; quantity: string; inward_product_id: string; inward_quantity: string; product_label?: string}>>([]);
  const [relatedOutwardIds, setRelatedOutwardIds] = useState<number[]>([]);
  const [openOutwards, setOpenOutwards] = useState<any[]>([]);
  const [outwardSearchText, setOutwardSearchText] = useState('');

  // Stock availability state
  const [stockInfo, setStockInfo] = useState<Record<string, { available: number; sufficient: boolean }>>({});
  const [stockCheckError, setStockCheckError] = useState<string>('');
  const [showOutwardDropdown, setShowOutwardDropdown] = useState(false);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  useEffect(() => { fetchOrders(); }, [debouncedSearch, filterType, filterStatus, filterDecorator, dateFrom, dateTo]);
  useEffect(() => { fetchWarehouses(); fetchDecoratorOptions(); }, []);

  const fetchDecoratorOptions = async () => {
    try {
      const res = await api.get('/inventory/job-orders/', { params: { limit: 500 } });
      const names = (res.data || [])
        .map((jo: any) => jo.decorator_name)
        .filter((name: string) => name && name.trim());
      setDecoratorOptions([...new Set(names)] as string[]);
    } catch (e) {}
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params: any = { limit: 500 };
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      if (filterDecorator) params.decorator = filterDecorator;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/inventory/job-orders/', { params });
      setOrders(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get('/products/?limit=200');
      setProducts(res.data.products || []);
    } catch (e) {}
  };

  const fetchWarehouses = async () => {
    try {
      const res = await api.get('/inventory/warehouses/');
      const whs = res.data || [];
      setWarehouses(whs);
      const defaultWh = whs.find((w: any) => w.is_default);
      if (defaultWh) setSelectedWarehouseId(defaultWh.id);
      else if (whs.length > 0) setSelectedWarehouseId(whs[0].id);
    } catch (e) {}
  };

  const fetchOpenOutwards = async () => {
    try {
      const res = await api.get('/inventory/job-orders/open-outward');
      setOpenOutwards(res.data || []);
    } catch (e) {}
  };

  // Check stock availability for outward job order items
  const checkStockAvailability = async (items: Array<{product_id: string; quantity: string}>) => {
    if (formType !== 'outward') {
      setStockInfo({});
      setStockCheckError('');
      return;
    }

    const validItems = items.filter(i => i.product_id && parseInt(i.quantity) > 0);
    if (validItems.length === 0) {
      setStockInfo({});
      setStockCheckError('');
      return;
    }

    try {
      const res = await api.post('/inventory/job-orders/check-stock', {
        items: validItems.map(i => ({ product_id: parseInt(i.product_id), quantity: parseInt(i.quantity) })),
        warehouse_id: selectedWarehouseId || undefined
      });
      const data = res.data;
      const newStockInfo: Record<string, { available: number; sufficient: boolean }> = {};
      for (const item of data.items) {
        newStockInfo[String(item.product_id)] = {
          available: item.available_quantity,
          sufficient: item.sufficient
        };
      }
      setStockInfo(newStockInfo);
      if (!data.all_sufficient) {
        const insufficientItems = data.items
          .filter((i: any) => !i.sufficient)
          .map((i: any) => `${i.product_sku || 'Unknown'} (available: ${i.available_quantity}, requested: ${i.requested_quantity})`)
          .join(', ');
        setStockCheckError(`Insufficient stock: ${insufficientItems}`);
      } else {
        setStockCheckError('');
      }
    } catch (e) {
      // Don't block on check failure — backend will enforce at creation time
      setStockInfo({});
      setStockCheckError('');
    }
  };

  // Debounce stock check when form items change (outward only)
  useEffect(() => {
    if (!showForm || formType !== 'outward') return;
    const timeout = setTimeout(() => {
      checkStockAvailability(formItems);
    }, 500);
    return () => clearTimeout(timeout);
  }, [formItems, formType, showForm, selectedWarehouseId]);

  const filteredOutwards = openOutwards.filter(ow => {
    if (!outwardSearchText.trim()) return true;
    const q = outwardSearchText.toLowerCase();
    return (ow.job_order_number || '').toLowerCase().includes(q) ||
           (ow.decorator_name || '').toLowerCase().includes(q) ||
           (ow.items || []).some((i: any) => (i.product_sku || '').toLowerCase().includes(q));
  });

  // Orders are already filtered server-side via search + type + status
  const filteredOrders = orders;

  const openForm = (type: 'outward' | 'inward') => {
    setFormType(type);
    setDecoratorName('');
    setDecorationType('');
    setExpectedReturn('');
    setFormNotes('');
    setFormItems([{ product_id: '', quantity: '', inward_product_id: '', inward_quantity: '' }]);
    setRelatedOutwardIds([]);
    setOutwardSearchText('');
    setStockInfo({});
    setStockCheckError('');
    fetchProducts();
    if (type === 'inward') fetchOpenOutwards();
    setShowForm(true);
  };

  // Change #8: Create Inward from Outward
  const handleCreateInwardFromOutward = (outwardOrder: JobOrder) => {
    setViewingJobOrder(null);
    setFormType('inward');
    setDecoratorName(outwardOrder.decorator_name || '');
    setDecorationType(outwardOrder.decoration_type || '');
    setExpectedReturn('');
    setFormNotes('');
    setRelatedOutwardIds([outwardOrder.id]);
    setOutwardSearchText('');
    setFormItems(outwardOrder.items.map(item => ({
      product_id: String(item.product_id),
      quantity: String(item.quantity),
      inward_product_id: '',
      inward_quantity: '',
      product_label: item.product_sku ? `${item.product_sku}${item.product_name ? ` - ${item.product_name}` : ''}` : undefined,
    })));
    fetchProducts();
    fetchOpenOutwards();
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (formItems.length === 0 || !formItems[0].product_id) return;
    
    // Calculate total value using Buying Price attribute
    let totalValue = 0;
    for (const item of formItems) {
      if (!item.product_id || !item.quantity) continue;
      const product = products.find(p => p.id === parseInt(item.product_id));
      if (product) {
        const buyingPriceAttr = product.attributes?.find(
          (a: any) => a.attribute_name?.toLowerCase() === 'buying price' || a.attribute_name?.toLowerCase() === 'buyingprice' || a.attribute_name?.toLowerCase() === 'buying_price'
        );
        const unitPrice = buyingPriceAttr ? parseFloat(buyingPriceAttr.value) || 0 : (product.price || 0);
        totalValue += unitPrice * (parseInt(item.quantity) || 0);
      }
    }
    
    // E-way bill alert if total value exceeds ₹50,000
    if (totalValue > 50000 && formType === 'outward') {
      const proceed = window.confirm(
        `⚠️ Total consignment value: ₹${totalValue.toLocaleString('en-IN')}\n\n` +
        `Since the value exceeds ₹50,000, you may need to generate an E-Way Bill before dispatching the goods.\n\n` +
        `Do you want to proceed with creating the job order?`
      );
      if (!proceed) return;
    }
    
    setSubmitting(true);
    try {
      const items = formItems.filter(i => i.product_id).map(i => ({
        product_id: parseInt(i.product_id),
        quantity: parseInt(i.quantity) || 0,
        inward_product_id: i.inward_product_id ? parseInt(i.inward_product_id) : undefined,
        inward_quantity: i.inward_quantity ? parseInt(i.inward_quantity) : undefined,
      }));

      await api.post('/inventory/job-orders/', {
        type: formType,
        decorator_name: decoratorName,
        decoration_type: decorationType,
        expected_return_date: expectedReturn || undefined,
        notes: formNotes || undefined,
        related_job_order_ids: relatedOutwardIds.length > 0 ? relatedOutwardIds : undefined,
        items
      });
      setShowForm(false);
      fetchOrders();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to create job order');
    } finally { setSubmitting(false); }
  };

  const handleConfirm = async (id: number) => {
    if (!window.confirm('Confirm this job order? Inventory will be updated.')) return;
    try {
      await api.post(`/inventory/job-orders/${id}/confirm`, { warehouse_id: selectedWarehouseId || undefined });
      fetchOrders();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed to confirm'); }
  };

  const handleComplete = async (id: number) => {
    try {
      await api.post(`/inventory/job-orders/${id}/complete`);
      fetchOrders();
      setViewingJobOrder(null);
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id: number) => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/inventory/job-orders/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      fetchOrders();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed to delete'); }
  };

  const handleDeleteClick = (id: number, name: string) => {
    setDeleteConfirm({show: true, id, name});
  };

  const handleOpenEdit = (jobOrder: JobOrder) => {
    setEditingJobOrder(jobOrder);
    setEditForm({
      decorator_name: jobOrder.decorator_name || '',
      decoration_type: jobOrder.decoration_type || '',
      expected_return_date: jobOrder.expected_return_date ? jobOrder.expected_return_date.split('T')[0] : '',
      actual_return_date: jobOrder.actual_return_date ? jobOrder.actual_return_date.split('T')[0] : '',
      notes: jobOrder.notes || '',
    });
    setEditItems(jobOrder.items.map(item => ({
      product_id: item.product_id,
      product_sku: item.product_sku || '',
      product_name: item.product_name || '',
      quantity: item.quantity,
      inward_product_id: item.inward_product_id ? String(item.inward_product_id) : '',
      inward_quantity: item.inward_quantity ? String(item.inward_quantity) : '',
      inward_product_label: item.inward_product_sku ? `${item.inward_product_sku}${item.inward_product_name ? ` - ${item.inward_product_name}` : ''}` : undefined,
    })));
    setViewingJobOrder(null);
    fetchProducts();
  };

  const handleSaveEdit = async () => {
    if (!editingJobOrder) return;
    setEditSaving(true);
    try {
      const payload: any = {
        decorator_name: editForm.decorator_name,
        decoration_type: editForm.decoration_type,
        expected_return_date: editForm.expected_return_date || null,
        actual_return_date: editForm.actual_return_date || null,
        notes: editForm.notes,
      };
      // Include items if this is an inward order (to update decorated SKU/qty)
      if (editingJobOrder.type === 'inward') {
        payload.items = editItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          inward_product_id: item.inward_product_id ? parseInt(item.inward_product_id) : undefined,
          inward_quantity: item.inward_quantity ? parseInt(item.inward_quantity) : 0,
        }));
      }
      await api.put(`/inventory/job-orders/${editingJobOrder.id}`, payload);
      setEditingJobOrder(null);
      fetchOrders();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to update job order');
    } finally {
      setEditSaving(false);
    }
  };

  // DC (Delivery Challan) functions
  const openDCForm = async (jobOrder: JobOrder) => {
    // If DC already exists, download it directly instead of showing form
    if (jobOrder.dc_id) {
      try {
        const pdfRes = await api.get(`/delivery-challans/${jobOrder.dc_id}/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${(jobOrder.dc_number || 'DC').replace(/\//g, '-')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e: any) {
        alert(e.response?.data?.detail || 'Failed to download Delivery Challan');
      }
      return;
    }

    // Double-check with backend if a DC already exists (in case local state is stale)
    try {
      const checkRes = await api.get(`/delivery-challans/?job_order_id=${jobOrder.id}`);
      const existingDCs = Array.isArray(checkRes.data) ? checkRes.data : [];
      const matchingDC = existingDCs.find((dc: any) => dc.job_order_id === jobOrder.id);
      if (matchingDC) {
        // DC exists but local state didn't have it — download directly
        const pdfRes = await api.get(`/delivery-challans/${matchingDC.id}/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${(matchingDC.dc_number || 'DC').replace(/\//g, '-')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        // Update local state
        if (viewingJobOrder && viewingJobOrder.id === jobOrder.id) {
          setViewingJobOrder({ ...viewingJobOrder, dc_id: matchingDC.id, dc_number: matchingDC.dc_number });
        }
        fetchOrders();
        return;
      }
    } catch (e) {
      // If check fails, proceed to show form
    }

    // No DC exists yet — show creation form
    setDcJobOrder(jobOrder);
    setDcForm({
      to_name: jobOrder.decorator_name || '',
      to_address: '',
      to_city: '',
      to_state: '',
      to_pincode: '',
      to_gstin: '',
      vehicle_number: '',
      transport_mode: 'road',
      notes: '',
    });
    setVendorSearch(jobOrder.decorator_name || '');
    setShowDCForm(true);
    // Try to find matching vendor
    if (jobOrder.decorator_name) {
      searchVendors(jobOrder.decorator_name);
    }
  };

  const searchVendors = async (q: string) => {
    if (!q.trim()) { setVendorResults([]); return; }
    try {
      const res = await api.get('/vendors/', { params: { q: q.trim(), limit: 10 } });
      setVendorResults(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setVendorResults([]); }
  };

  const searchDecoratorVendors = async (q: string) => {
    if (!q.trim()) { setDecoratorVendorResults([]); setShowDecoratorDropdown(false); return; }
    try {
      const res = await api.get('/vendors/', { params: { q: q.trim(), limit: 10 } });
      const results = Array.isArray(res.data) ? res.data : [];
      setDecoratorVendorResults(results);
      setShowDecoratorDropdown(results.length > 0);
    } catch (e) { setDecoratorVendorResults([]); setShowDecoratorDropdown(false); }
  };

  const selectVendor = (vendor: any) => {
    setDcForm({
      ...dcForm,
      to_name: vendor.name || '',
      to_address: vendor.address || '',
      to_city: vendor.city || '',
      to_state: vendor.state || '',
      to_pincode: vendor.pincode || '',
      to_gstin: vendor.gstin || '',
    });
    setVendorSearch(vendor.name);
    setVendorResults([]);
  };

  const handleCreateDC = async () => {
    if (!dcJobOrder || !dcForm.to_name.trim()) { alert('Recipient name is required'); return; }
    setDcSaving(true);
    try {
      const dcType = dcJobOrder.type === 'outward' ? 'outward' : 'inward';
      const items = dcJobOrder.items.map(item => {
        const isInward = dcType === 'inward';
        const productId = isInward && item.inward_product_id ? item.inward_product_id : item.product_id;
        const description = isInward && item.inward_product_name
          ? `${item.inward_product_sku || ''} - ${item.inward_product_name}`.trim().replace(/^- /, '')
          : (item.product_name || `Product ${item.product_id}`);
        const quantity = isInward ? (item.inward_quantity || item.quantity) : item.quantity;
        return {
          product_id: productId,
          description,
          hsn_code: '',
          quantity,
          unit: 'Nos',
          value: 0,
        };
      });

      const payload = {
        dc_type: dcType,
        dc_date: new Date().toISOString(),
        job_order_id: dcJobOrder.id,
        from_name: 'LOGO GEAR SOLUTION LLP',
        from_address: '#1149, 20th Cross Mico Layout, Near Begur Main Road',
        from_city: 'Bengaluru',
        from_state: 'Karnataka',
        from_pincode: '560068',
        from_gstin: '29AALFL0083H1Z5',
        to_name: dcForm.to_name,
        to_address: dcForm.to_address,
        to_city: dcForm.to_city,
        to_state: dcForm.to_state,
        to_pincode: dcForm.to_pincode,
        to_gstin: dcForm.to_gstin,
        vehicle_number: dcForm.vehicle_number,
        transport_mode: dcForm.transport_mode,
        notes: dcForm.notes,
        items,
      };

      const res = await api.post('/delivery-challans/', payload);
      const dcId = res.data.id;

      // Download PDF
      const pdfRes = await api.get(`/delivery-challans/${dcId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${res.data.dc_number.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowDCForm(false);
      setDcJobOrder(null);
      alert(`Delivery Challan ${res.data.dc_number} created and downloaded.`);
      // Update the viewing job order with the new DC info so button shows "Download DC"
      if (viewingJobOrder && viewingJobOrder.id === dcJobOrder.id) {
        setViewingJobOrder({ ...viewingJobOrder, dc_id: dcId, dc_number: res.data.dc_number });
      }
      fetchOrders(); // Refresh to update dc_id/dc_number
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to create Delivery Challan');
    } finally { setDcSaving(false); }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = { draft: '#64748b', confirmed: '#f59e0b', completed: '#16a34a' };
    return <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, background: `${colors[status]}20`, color: colors[status] }}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
  };

  const getTypeBadge = (type: string) => {
    const color = type === 'outward' ? '#dc2626' : '#0d9488';
    return <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, background: `${color}15`, color }}>{type === 'outward' ? '↗ Outward' : '↙ Inward'}</span>;
  };

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedJobOrders = [...filteredOrders].sort((a, b) => {
    if (!sortField) return 0;
    let aVal: any;
    let bVal: any;
    if (sortField === 'items_count') {
      aVal = a.items.length;
      bVal = b.items.length;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (sortField === 'total_qty') {
      aVal = a.items.reduce((sum, i) => sum + (a.type === 'inward' ? (i.inward_quantity || i.quantity) : i.quantity), 0);
      bVal = b.items.reduce((sum, i) => sum + (b.type === 'inward' ? (i.inward_quantity || i.quantity) : i.quantity), 0);
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (sortField === 'received_qty') {
      aVal = a.received_qty || 0;
      bVal = b.received_qty || 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (sortField === 'decorator_name') {
      aVal = (a.decorator_name || '').toLowerCase();
      bVal = (b.decorator_name || '').toLowerCase();
    } else {
      aVal = (a as any)[sortField];
      bVal = (b as any)[sortField];
    }
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div>
      <div className="card-header" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <h2 className="card-title" style={{ fontSize: '16px' }}>Job Orders</h2>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: '200px' }}>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search JO #, SKU, decorator, type, status..."
            style={{ padding: '6px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px', width: '100%', maxWidth: '400px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-primary btn-sm" onClick={() => openForm('inward')} style={{ fontSize: '12px' }}>↙ Record Inward</button>
          <button className="btn btn-primary btn-sm" onClick={() => openForm('outward')} style={{ fontSize: '12px' }}>↗ New Outward</button>
        </div>
      </div>

      {/* Filter bar */}
      <CollapsibleFilters label="Filters">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
            >
              <option value="">All Types</option>
              <option value="outward">Outward</option>
              <option value="inward">Inward</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
            >
              <option value="draft,confirmed">Open (Draft/Confirmed)</option>
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Decorator</label>
            <select
              value={filterDecorator}
              onChange={(e) => setFilterDecorator(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
            >
              <option value="">All Decorators</option>
              {decoratorOptions.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>From Date</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>To Date</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
          </div>
          <button onClick={() => { setFilterType(''); setFilterStatus('draft,confirmed'); setFilterDecorator(''); setSearchText(''); setDateFrom(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; }); setDateTo(new Date().toISOString().split('T')[0]); }} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>
            Reset
          </button>
        </div>
      </CollapsibleFilters>

      <div className="table-container">
        {loading ? <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div> : (
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => handleSort('job_order_number')} style={{ cursor: 'pointer' }}>Job Order # {sortField === 'job_order_number' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>Type {sortField === 'type' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                <th onClick={() => handleSort('decorator_name')} style={{ cursor: 'pointer' }}>Decorator {sortField === 'decorator_name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                <th onClick={() => handleSort('items_count')} style={{ cursor: 'pointer' }}>Items {sortField === 'items_count' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                <th onClick={() => handleSort('total_qty')} style={{ cursor: 'pointer' }}>Total Qty {sortField === 'total_qty' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                <th onClick={() => handleSort('received_qty')} style={{ cursor: 'pointer' }}>Received Qty {sortField === 'received_qty' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>Created Date {sortField === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                <th onClick={() => handleSort('expected_return_date')} style={{ cursor: 'pointer' }}>Expected Date {sortField === 'expected_return_date' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobOrders.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No job orders found</td></tr>
              ) : sortedJobOrders.map(jo => (
                <tr key={jo.id} onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest('button')) return;
                  setViewingJobOrder(jo);
                }} style={{ cursor: 'pointer' }}>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 500 }}>{jo.job_order_number}</td>
                  <td>{getTypeBadge(jo.type)}</td>
                  <td>{jo.decorator_name || '—'}</td>
                  <td>{jo.items.length} SKU{jo.items.length !== 1 ? 's' : ''}</td>
                  <td style={{ fontWeight: 600 }}>{formatIndian(jo.items.reduce((sum, i) => sum + (jo.type === 'inward' ? (i.inward_quantity || i.quantity) : i.quantity), 0))}</td>
                  <td style={{ fontWeight: 500, color: '#0d9488' }}>{jo.received_qty ? formatIndian(jo.received_qty) : '—'}</td>
                  <td>{getStatusBadge(jo.status)}</td>
                  <td style={{ fontSize: '12px', color: '#64748b' }}>{jo.created_at ? new Date(jo.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '—'}</td>
                  <td style={{ fontSize: '12px', color: '#64748b' }}>{jo.expected_return_date ? new Date(jo.expected_return_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {jo.status === 'draft' && (
                        <>
                          <button className="btn btn-primary" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => handleConfirm(jo.id)}>Confirm</button>
                          <button className="btn btn-danger" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => handleDeleteClick(jo.id, jo.job_order_number || `Job Order #${jo.id}`)}>Delete</button>
                        </>
                      )}
                      {jo.status === 'confirmed' && (
                        <button className="btn btn-primary" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => handleComplete(jo.id)}>Complete</button>
                      )}
                      {jo.status !== 'draft' && userRole === 'admin' && (
                        <button className="btn btn-danger" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => handleDelete(jo.id)}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Job Order Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '650px', maxHeight: '85vh', overflow: 'auto' }}>
            <button className="modal-close" onClick={() => setShowForm(false)} aria-label="Close">×</button>
            <h3>{formType === 'outward' ? '↗ Send for Decoration (Outward)' : '↙ Receive from Decoration (Inward)'}</h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Warehouse *</label>
              <select
                value={selectedWarehouseId}
                onChange={(e) => setSelectedWarehouseId(parseInt(e.target.value))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}
              >
                <option value={0}>Select Warehouse</option>
                {warehouses.map(wh => (
                  <option key={wh.id} value={wh.id}>{wh.name}</option>
                ))}
              </select>
            </div>

            <div className="form-grid-2">
              <div style={{ position: 'relative' }}>
                <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Decorator Name</label>
                <input
                  type="text"
                  value={decoratorName}
                  onChange={(e) => {
                    setDecoratorName(e.target.value);
                    searchDecoratorVendors(e.target.value);
                  }}
                  onFocus={() => { if (decoratorName.trim()) searchDecoratorVendors(decoratorName); }}
                  onBlur={() => setTimeout(() => setShowDecoratorDropdown(false), 200)}
                  placeholder="Type to search vendors..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}
                />
                {showDecoratorDropdown && decoratorVendorResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '160px', overflow: 'auto' }}>
                    {decoratorVendorResults.map(v => (
                      <div
                        key={v.id}
                        onMouseDown={() => { setDecoratorName(v.name); setShowDecoratorDropdown(false); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}
                        onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                        onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
                      >
                        {v.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Decoration Type</label>
                <input type="text" value={decorationType} onChange={(e) => setDecorationType(e.target.value)} placeholder="Printing, Embroidery..." style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>
            </div>

            {formType === 'outward' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Expected Return Date</label>
                <input type="date" value={expectedReturn} onChange={(e) => setExpectedReturn(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>
            )}

            {formType === 'inward' && (
              <div style={{ marginBottom: '16px', position: 'relative' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Related Outward Job Orders</label>
                {/* Selected outward orders as chips */}
                {relatedOutwardIds.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {relatedOutwardIds.map(id => {
                      const ow = openOutwards.find((o: any) => o.id === id);
                      return (
                        <span key={id} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '4px 10px', background: '#dbeafe', color: '#1e40af',
                          borderRadius: '6px', fontSize: '12px', fontWeight: 500
                        }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{ow?.job_order_number || `#${id}`}</span>
                          {ow?.decorator_name && <span style={{ color: '#3b82f6' }}>— {ow.decorator_name}</span>}
                          <button
                            type="button"
                            onClick={() => {
                              const newIds = relatedOutwardIds.filter(oid => oid !== id);
                              setRelatedOutwardIds(newIds);
                              // Rebuild items from remaining selected outwards
                              const remainingOutwards = openOutwards.filter((o: any) => newIds.includes(o.id));
                              const newItems = remainingOutwards.flatMap((o: any) =>
                                o.items.map((item: any) => ({
                                  product_id: String(item.product_id),
                                  quantity: String(item.quantity),
                                  inward_product_id: '',
                                  inward_quantity: '',
                                  product_label: item.product_sku ? `${item.product_sku}${item.product_name ? ` - ${item.product_name}` : ''}` : undefined,
                                }))
                              );
                              setFormItems(newItems.length > 0 ? newItems : [{ product_id: '', quantity: '', inward_product_id: '', inward_quantity: '' }]);
                            }}
                            style={{ background: 'none', border: 'none', color: '#1e40af', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0 2px' }}
                            aria-label="Remove"
                          >×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <input
                  type="text"
                  value={outwardSearchText}
                  onChange={(e) => {
                    setOutwardSearchText(e.target.value);
                    setShowOutwardDropdown(true);
                  }}
                  onFocus={() => setShowOutwardDropdown(true)}
                  onBlur={() => setTimeout(() => setShowOutwardDropdown(false), 200)}
                  placeholder={relatedOutwardIds.length > 0 ? 'Add another outward order...' : 'Type job order number or decorator name...'}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}
                />
                {showOutwardDropdown && filteredOutwards.filter((ow: any) => !relatedOutwardIds.includes(ow.id)).length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '180px', overflow: 'auto' }}>
                    {filteredOutwards.filter((ow: any) => !relatedOutwardIds.includes(ow.id)).map((ow: any) => (
                      <div
                        key={ow.id}
                        onMouseDown={() => {
                          const newIds = [...relatedOutwardIds, ow.id];
                          setRelatedOutwardIds(newIds);
                          setOutwardSearchText('');
                          setShowOutwardDropdown(false);
                          // Aggregate items from all selected outward orders
                          const selectedOutwards = openOutwards.filter((o: any) => newIds.includes(o.id));
                          const aggregatedItems = selectedOutwards.flatMap((o: any) =>
                            o.items.map((item: any) => ({
                              product_id: String(item.product_id),
                              quantity: String(item.quantity),
                              inward_product_id: '',
                              inward_quantity: '',
                              product_label: item.product_sku ? `${item.product_sku}${item.product_name ? ` - ${item.product_name}` : ''}` : undefined,
                            }))
                          );
                          setFormItems(aggregatedItems.length > 0 ? aggregatedItems : [{ product_id: '', quantity: '', inward_product_id: '', inward_quantity: '' }]);
                          // Set decorator info from first selected outward (if not already set)
                          if (!decoratorName && ow.decorator_name) setDecoratorName(ow.decorator_name);
                          if (!decorationType && ow.decoration_type) setDecorationType(ow.decoration_type);
                        }}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}
                        onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                        onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
                      >
                        <div style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '12px' }}>{ow.job_order_number}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{ow.decorator_name} ({ow.items.map((i: any) => i.product_sku).join(', ')})</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Items */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600 }}>Items</label>
                <button className="btn btn-secondary" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => setFormItems([...formItems, { product_id: '', quantity: '', inward_product_id: '', inward_quantity: '' }])}>+ Add Item</button>
              </div>
              {formItems.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ flex: 2 }}>
                    <ProductTypeahead
                      value={item.product_id}
                      knownProducts={products}
                      initialLabel={item.product_label}
                      labelFor={getProductLabel}
                      placeholder={formType === 'outward' ? 'Type SKU or product name...' : 'Type source SKU or name...'}
                      onSelect={(p) => {
                        const newItems = [...formItems];
                        newItems[idx].product_id = p ? String(p.id) : '';
                        newItems[idx].product_label = p ? getProductLabel(p) : undefined;
                        setFormItems(newItems);
                      }}
                    />
                  </div>
                  <div style={{ width: '70px' }}>
                    <input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => { const n = [...formItems]; n[idx].quantity = e.target.value; setFormItems(n); }} min="1" style={{ width: '100%', padding: '7px 10px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '12px' }} />
                  </div>
                  {/* Stock availability indicator for outward orders */}
                  {formType === 'outward' && item.product_id && stockInfo[item.product_id] !== undefined && (
                    <div style={{ display: 'flex', alignItems: 'center', minWidth: '90px' }}>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '4px 8px',
                        borderRadius: '6px',
                        background: stockInfo[item.product_id].sufficient ? '#d4edda' : '#f8d7da',
                        color: stockInfo[item.product_id].sufficient ? '#155724' : '#721c24',
                      }}>
                        {stockInfo[item.product_id].sufficient
                          ? `Stock: ${stockInfo[item.product_id].available}`
                          : `Only ${stockInfo[item.product_id].available} avail.`}
                      </span>
                    </div>
                  )}
                  {formType === 'inward' && (
                    <>
                      <div style={{ flex: 2 }}>
                        <ProductTypeahead
                          value={item.inward_product_id}
                          knownProducts={products}
                          labelFor={getProductLabel}
                          placeholder="Type decorated SKU or name..."
                          onSelect={(p) => {
                            const n = [...formItems];
                            n[idx].inward_product_id = p ? String(p.id) : '';
                            setFormItems(n);
                          }}
                        />
                      </div>
                      <div style={{ width: '70px' }}>
                        <input type="number" placeholder="Recv" value={item.inward_quantity} onChange={(e) => { const n = [...formItems]; n[idx].inward_quantity = e.target.value; setFormItems(n); }} min="0" style={{ width: '100%', padding: '7px 10px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '12px' }} />
                      </div>
                    </>
                  )}
                  {formItems.length > 1 && <button onClick={() => setFormItems(formItems.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px' }}>×</button>}
                </div>
              ))}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Notes</label>
              <textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} rows={2} placeholder="Any notes..." style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit' }} />
            </div>

            {/* Insufficient stock warning */}
            {formType === 'outward' && stockCheckError && (
              <div style={{
                marginBottom: '16px',
                padding: '10px 14px',
                background: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#721c24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <span style={{ fontSize: '12px', color: '#721c24', fontWeight: 500 }}>{stockCheckError}</span>
              </div>
            )}

            {/* Total Value Display (for outward) */}
            {formType === 'outward' && formItems.some(i => i.product_id && i.quantity) && (() => {
              let total = 0;
              for (const item of formItems) {
                if (!item.product_id || !item.quantity) continue;
                const p = products.find(pr => pr.id === parseInt(item.product_id));
                if (p) {
                  const bp = p.attributes?.find((a: any) => a.attribute_name?.toLowerCase() === 'buying price' || a.attribute_name?.toLowerCase() === 'buyingprice' || a.attribute_name?.toLowerCase() === 'buying_price');
                  const price = bp ? parseFloat(bp.value) || 0 : (p.price || 0);
                  total += price * (parseInt(item.quantity) || 0);
                }
              }
              return total > 0 ? (
                <div style={{ marginBottom: '12px', padding: '10px 14px', background: total > 50000 ? '#fef3c7' : '#f0fdf4', border: `1px solid ${total > 50000 ? '#f59e0b' : '#bbf7d0'}`, borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>Estimated Consignment Value:</span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: total > 50000 ? '#92400e' : '#166534' }}>
                    ₹{total.toLocaleString('en-IN')}
                    {total > 50000 && <span style={{ fontSize: '11px', fontWeight: 500, marginLeft: '8px', color: '#b45309' }}>E-Way Bill may be required</span>}
                  </span>
                </div>
              ) : null;
            })()}

            <div className="form-actions">
              <button className="btn btn-cancel" onClick={() => setShowForm(false)} disabled={submitting}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={submitting || !formItems[0]?.product_id || !formItems[0]?.quantity || (formType === 'outward' && !!stockCheckError)}
                title={formType === 'outward' && stockCheckError ? 'Cannot create: insufficient stock' : ''}
              >
                {submitting ? 'Creating...' : 'Create Job Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingJobOrder && (
        <div className="modal-overlay" onClick={() => setViewingJobOrder(null)}>
          <div className="modal" style={{ maxWidth: '750px', position: 'relative', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setViewingJobOrder(null)}
              style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}
              aria-label="Close"
            >×</button>
            <h3 style={{ marginBottom: '1.25rem' }}>Job Order Details</h3>
            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.25rem', border: '1px solid #e2e8f0' }}>
              <div className="form-grid-2" style={{ marginBottom: 0 }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Job Order #</div>
                  <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{viewingJobOrder.job_order_number}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Type</div>
                  <div>{getTypeBadge(viewingJobOrder.type)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Decorator</div>
                  <div style={{ fontWeight: 500 }}>{viewingJobOrder.decorator_name || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Decoration Type</div>
                  <div style={{ fontWeight: 500 }}>{viewingJobOrder.decoration_type || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Status</div>
                  <div>{getStatusBadge(viewingJobOrder.status)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Expected Return Date</div>
                  <div style={{ fontWeight: 500 }}>{viewingJobOrder.expected_return_date ? new Date(viewingJobOrder.expected_return_date).toLocaleDateString('en-IN') : '—'}</div>
                </div>
                {viewingJobOrder.type === 'inward' && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Actual Return Date</div>
                    <div style={{ fontWeight: 500, color: viewingJobOrder.actual_return_date ? '#16a34a' : '#94a3b8' }}>{viewingJobOrder.actual_return_date ? new Date(viewingJobOrder.actual_return_date).toLocaleDateString('en-IN') : '—'}</div>
                  </div>
                )}
                {viewingJobOrder.notes && (
                  <div className="span-2">
                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Notes</div>
                    <div style={{ fontWeight: 500 }}>{viewingJobOrder.notes}</div>
                  </div>
                )}
                {viewingJobOrder.linked_outwards && viewingJobOrder.linked_outwards.length > 0 && (
                  <div className="span-2">
                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Related Outward Orders</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {viewingJobOrder.linked_outwards.map((ow) => (
                        <span key={ow.id} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '4px 10px', background: '#dbeafe', color: '#1e40af',
                          borderRadius: '6px', fontSize: '12px', fontWeight: 500
                        }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{ow.job_order_number}</span>
                          {ow.decorator_name && <span style={{ color: '#3b82f6', fontSize: '11px' }}>— {ow.decorator_name}</span>}
                          <span style={{
                            fontSize: '10px', padding: '1px 5px', borderRadius: '4px',
                            background: ow.status === 'completed' ? '#d4edda' : ow.status === 'confirmed' ? '#cce5ff' : '#f8f9fa',
                            color: ow.status === 'completed' ? '#155724' : ow.status === 'confirmed' ? '#004085' : '#383d41'
                          }}>{ow.status}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {!viewingJobOrder.linked_outwards?.length && viewingJobOrder.related_job_order_number && (
                  <div className="span-2">
                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Related Outward Order</div>
                    <div style={{ fontWeight: 600, fontFamily: 'monospace', color: '#0d9488' }}>{viewingJobOrder.related_job_order_number}</div>
                  </div>
                )}
              </div>
            </div>

            <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '14px' }}>Items</h4>
            <table className="table" style={{ fontSize: '13px' }}>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product Name</th>
                  <th>Quantity</th>
                  {viewingJobOrder.type === 'inward' && <th>Decorated SKU</th>}
                  {viewingJobOrder.type === 'inward' && <th>Received Qty</th>}
                </tr>
              </thead>
              <tbody>
                {viewingJobOrder.items?.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{item.product_sku || '—'}</td>
                    <td>{item.product_name || '—'}</td>
                    <td style={{ fontWeight: 600 }}>{formatIndian(item.quantity)}</td>
                    {viewingJobOrder.type === 'inward' && <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{item.inward_product_sku || '—'}</td>}
                    {viewingJobOrder.type === 'inward' && <td style={{ fontWeight: 600 }}>{item.inward_quantity != null ? formatIndian(item.inward_quantity) : '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Linked Inward Orders for Outward */}
            {viewingJobOrder.type === 'outward' && viewingJobOrder.linked_inwards && viewingJobOrder.linked_inwards.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{ fontSize: '14px', marginBottom: '0.75rem' }}>Linked Inward Orders</h4>
                <div style={{ background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0', padding: '12px' }}>
                  {viewingJobOrder.linked_inwards.map((inward) => (
                    <div key={inward.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #dcfce7' }}>
                      <div>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '12px', color: '#0d9488' }}>{inward.job_order_number}</span>
                        <span style={{ marginLeft: '8px', fontSize: '11px', color: '#64748b' }}>{inward.created_at ? new Date(inward.created_at).toLocaleDateString('en-IN') : ''}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600 }}>{formatIndian(inward.total_qty)} pcs</span>
                        {getStatusBadge(inward.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '10px', alignItems: 'center' }}>
              {viewingJobOrder.type === 'outward' && viewingJobOrder.status === 'confirmed' && (
                <button
                  className="btn btn-secondary"
                  style={{ fontSize: '12px' }}
                  onClick={() => handleCreateInwardFromOutward(viewingJobOrder)}
                >
                  ↙ Create Inward
                </button>
              )}
              {viewingJobOrder.status === 'confirmed' && viewingJobOrder.type === 'outward' && viewingJobOrder.linked_inwards && viewingJobOrder.linked_inwards.length > 0 && (viewingJobOrder.received_qty || 0) >= viewingJobOrder.items.reduce((sum, i) => sum + i.quantity, 0) && (
                <button
                  className="btn btn-primary"
                  style={{ fontSize: '12px', background: '#16a34a' }}
                  onClick={() => handleComplete(viewingJobOrder.id)}
                >
                  Mark Complete
                </button>
              )}
              {viewingJobOrder.status === 'confirmed' && viewingJobOrder.type === 'outward' && viewingJobOrder.linked_inwards && viewingJobOrder.linked_inwards.length > 0 && (viewingJobOrder.received_qty || 0) < viewingJobOrder.items.reduce((sum, i) => sum + i.quantity, 0) && (
                <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 500 }}>
                  Received {viewingJobOrder.received_qty || 0} of {viewingJobOrder.items.reduce((sum, i) => sum + i.quantity, 0)} — awaiting full return
                </span>
              )}
              <button
                className="btn btn-primary"
                style={{ fontSize: '12px' }}
                onClick={() => openDCForm(viewingJobOrder)}
              >
                {viewingJobOrder.dc_id ? 'Download DC' : 'Generate DC'}
              </button>
              <button
                className="btn btn-secondary"
                style={{ fontSize: '12px' }}
                onClick={() => handleOpenEdit(viewingJobOrder)}
              >
                Edit
              </button>
              <button className="btn btn-secondary" onClick={() => setViewingJobOrder(null)} style={{ marginLeft: 'auto' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Challan Modal */}
      {showDCForm && dcJobOrder && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '650px', width: '95%', maxHeight: '90vh', overflow: 'auto' }}>
            <h3>Create Delivery Challan</h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 16px' }}>
              {dcJobOrder.type === 'outward' ? 'Outward' : 'Inward'} DC for Job Order <strong>{dcJobOrder.job_order_number}</strong>
            </p>

            {/* From (Principal - Logogear) - auto-filled */}
            <div style={{ background: '#f8fafc', borderRadius: '6px', padding: '12px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '6px' }}>From (Principal)</div>
              <div style={{ fontSize: '13px', fontWeight: 600 }}>LOGO GEAR SOLUTION LLP</div>
              <div style={{ fontSize: '12px', color: '#475569' }}>#1149, 20th Cross Mico Layout, Near Begur Main Road</div>
              <div style={{ fontSize: '12px', color: '#475569' }}>Bengaluru 560068, Karnataka</div>
              <div style={{ fontSize: '12px', color: '#475569', fontFamily: 'monospace' }}>GSTIN: 29AALFL0083H1Z5</div>
            </div>

            {/* To (Job Worker) - vendor type-ahead */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>To (Job Worker / Vendor) *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={vendorSearch}
                  onChange={(e) => {
                    setVendorSearch(e.target.value);
                    setDcForm({ ...dcForm, to_name: e.target.value });
                    searchVendors(e.target.value);
                  }}
                  placeholder="Search vendor or type name..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}
                />
                {vendorResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '180px', overflow: 'auto' }}>
                    {vendorResults.map(v => (
                      <div key={v.id} onClick={() => selectVendor(v)} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }} onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')} onMouseOut={(e) => (e.currentTarget.style.background = 'white')}>
                        <div style={{ fontWeight: 500 }}>{v.name}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{[v.city, v.state, v.gstin].filter(Boolean).join(' | ')}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-grid-2">
              <div className="span-2">
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Address</label>
                <input type="text" value={dcForm.to_address} onChange={(e) => setDcForm({ ...dcForm, to_address: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>City</label>
                <input type="text" value={dcForm.to_city} onChange={(e) => setDcForm({ ...dcForm, to_city: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>State</label>
                <input type="text" value={dcForm.to_state} onChange={(e) => setDcForm({ ...dcForm, to_state: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Pincode</label>
                <input type="text" value={dcForm.to_pincode} onChange={(e) => setDcForm({ ...dcForm, to_pincode: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>GSTIN</label>
                <input type="text" value={dcForm.to_gstin} onChange={(e) => setDcForm({ ...dcForm, to_gstin: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>
            </div>

            {/* Items preview */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Items (from Job Order)</label>
              <div style={{ background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', padding: '8px' }}>
                {dcJobOrder.items.map((item, idx) => {
                  const isInward = dcJobOrder.type === 'inward';
                  const displaySku = isInward && item.inward_product_sku ? item.inward_product_sku : item.product_sku;
                  const displayName = isInward && item.inward_product_name ? item.inward_product_name : item.product_name;
                  const displayQty = isInward ? (item.inward_quantity || item.quantity) : item.quantity;
                  return (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: '12px', borderBottom: idx < dcJobOrder.items.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                    <span>{displaySku} - {displayName}</span>
                    <span style={{ fontWeight: 600 }}>{displayQty} Nos</span>
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Transport details */}
            <div className="form-grid-2">
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Vehicle Number</label>
                <input type="text" value={dcForm.vehicle_number} onChange={(e) => setDcForm({ ...dcForm, vehicle_number: e.target.value })} placeholder="KA-01-XX-1234" style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Transport Mode</label>
                <select value={dcForm.transport_mode} onChange={(e) => setDcForm({ ...dcForm, transport_mode: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="road">Road</option>
                  <option value="courier">Courier</option>
                  <option value="hand_delivery">Hand Delivery</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Notes</label>
              <textarea value={dcForm.notes} onChange={(e) => setDcForm({ ...dcForm, notes: e.target.value })} rows={2} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit' }} />
            </div>

            <div className="form-actions">
              <button className="btn btn-cancel" onClick={() => { setShowDCForm(false); setDcJobOrder(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreateDC} disabled={dcSaving} style={{ background: '#7c3aed' }}>
                {dcSaving ? 'Generating...' : 'Generate DC & Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Job Order Modal */}
      {editingJobOrder && (
        <div className="modal-overlay" onClick={() => setEditingJobOrder(null)}>
          <div className="modal" style={{ maxWidth: '750px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1rem' }}>Edit Job Order — {editingJobOrder.job_order_number}</h3>

            <div className="form-grid-2">
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Decorator Name</label>
                <input type="text" value={editForm.decorator_name} onChange={(e) => setEditForm({ ...editForm, decorator_name: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Decoration Type</label>
                <input type="text" value={editForm.decoration_type} onChange={(e) => setEditForm({ ...editForm, decoration_type: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Expected Return Date</label>
                <input type="date" value={editForm.expected_return_date} onChange={(e) => setEditForm({ ...editForm, expected_return_date: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              {editingJobOrder.type === 'inward' && (
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Actual Return Date</label>
                  <input type="date" value={editForm.actual_return_date} onChange={(e) => setEditForm({ ...editForm, actual_return_date: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                </div>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Notes</label>
              <textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit' }} />
            </div>

            {/* Items section for inward job orders */}
            {editingJobOrder.type === 'inward' && editItems.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Items — Decorated SKU & Received Quantity</label>
                {/* NOTE: no `overflow: hidden` here — it would clip the decorated-SKU
                    typeahead's absolutely-positioned dropdown, making the lookahead
                    results invisible. Rounded corners are applied to the table instead. */}
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafb' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Source SKU</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Qty Sent</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Decorated SKU</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Qty Received</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editItems.map((item, idx) => (
                        <tr key={idx} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '6px 10px' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{item.product_sku}</span>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>{item.product_name}</div>
                          </td>
                          <td style={{ padding: '6px 10px', fontWeight: 600 }}>{item.quantity}</td>
                          <td style={{ padding: '6px 10px' }}>
                            <ProductTypeahead
                              value={item.inward_product_id}
                              knownProducts={products}
                              initialLabel={item.inward_product_label}
                              placeholder="Type decorated SKU..."
                              onSelect={(p) => {
                                const newItems = [...editItems];
                                newItems[idx].inward_product_id = p ? String(p.id) : '';
                                newItems[idx].inward_product_label = p ? `${p.sku} - ${p.name}` : undefined;
                                setEditItems(newItems);
                              }}
                            />
                          </td>
                          <td style={{ padding: '6px 10px' }}>
                            <input
                              type="number"
                              min="0"
                              value={item.inward_quantity}
                              onChange={(e) => { const newItems = [...editItems]; newItems[idx].inward_quantity = e.target.value; setEditItems(newItems); }}
                              style={{ width: '70px', padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px' }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button className="btn btn-cancel" onClick={() => setEditingJobOrder(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveEdit} disabled={editSaving}>
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Job Order"
        message="Are you sure you want to delete this job order? This action cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDelete as any}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default JobOrderManager;
